import * as React from 'react'
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import { DateTime, Info } from 'luxon'
import { CalendarProps, CalendarStrings, DayOfWeek, FirstWeekOfYear } from '@fluentui/react-calendar-compat'
import { DatePicker, DatePickerProps } from '@fluentui/react-datepicker-compat'
import { Button, makeStyles, mergeClasses, tokens, Tooltip, useFieldContext_unstable } from '@fluentui/react-components'
import { bundleIcon, CalendarMonthRegular, DismissFilled, DismissRegular } from '@fluentui/react-icons'
import { formatFullNumericDate, formatIsoDate, parseDate } from '../../helpers/dateHelper'
import { isDateFieldClearable } from '../../helpers/datePickerHelper'
import { useTranslation } from 'react-i18next'

const DismissIcon = bundleIcon(DismissFilled, DismissRegular)

export type LocalDatePickerProps = Omit<DatePickerProps, 'value' | 'onSelectDate' | 'placeholder'> & {
    isoValue?: string
    onSelectDate?: (isoDate: string) => void
    /** Translation key of the placeholder; defaults to base.datepicker.placeholder. */
    placeholderKey?: string
    /** The placeholder text itself; wins over placeholderKey so a form can hand over a composed one. */
    placeholder?: string
    /** Appended to the placeholder, for instance to mark the field as required. */
    placeholderSuffix?: string
}

const capitalize = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1)

const useStyles = makeStyles({
    /**
     * The cross keeps its place in the field so the date and the calendar icon never move; it only becomes visible
     * while the user is at the field. Focus counts as well, otherwise the keyboard and a touch screen could never
     * reach it.
     */
    root: {
        '& [data-clear-date]': { visibility: 'hidden' },
        '&:hover [data-clear-date]': { visibility: 'visible' },
        '&:focus-within [data-clear-date]': { visibility: 'visible' },
    },
    /** An empty field has nothing to clear, so the cross stays away even while the user is at the field. */
    rootEmpty: {
        '&:hover [data-clear-date]': { visibility: 'hidden' },
        '&:focus-within [data-clear-date]': { visibility: 'hidden' },
    },
    clear: {
        minWidth: 'unset',
        paddingLeft: tokens.spacingHorizontalXXS,
        paddingRight: tokens.spacingHorizontalXXS,
    },
})

/**
 * Month and day names of the calendar, in the language the user picked. They used to be hardcoded Dutch, which showed
 * a Dutch calendar to an English user (HAM-4275).
 */
export function useCalendarStrings(): CalendarStrings {
    const { t: translate, i18n } = useTranslation()

    return useMemo(() => {
        const locale = i18n.language
        // Luxon starts its week on Monday, the calendar expects the names to start on Sunday.
        const fromSunday = (names: string[]): string[] => [names[6], ...names.slice(0, 6)]

        return {
            months: Info.months('long', { locale }).map(capitalize),
            shortMonths: Info.months('short', { locale }).map(capitalize),
            days: fromSunday(Info.weekdays('long', { locale })).map(capitalize),
            shortDays: fromSunday(Info.weekdays('narrow', { locale })).map(capitalize),
            goToToday: translate('base.datepicker.gototoday'),
            prevMonthAriaLabel: translate('base.datepicker.prevmonth'),
            nextMonthAriaLabel: translate('base.datepicker.nextmonth'),
            prevYearAriaLabel: translate('base.datepicker.prevyear'),
            nextYearAriaLabel: translate('base.datepicker.nextyear'),
        }
    }, [i18n.language])
}

export const defaultCalendarProps: Pick<
    CalendarProps,
    'showWeekNumbers' | 'firstDayOfWeek' | 'firstWeekOfYear' | 'isMonthPickerVisible' | 'showMonthPickerAsOverlay'
> = {
    showWeekNumbers: true,
    firstDayOfWeek: DayOfWeek.Monday,
    firstWeekOfYear: FirstWeekOfYear.FirstDay,
    isMonthPickerVisible: false,
    showMonthPickerAsOverlay: true,
}

export const defaultDatePickerProps: Pick<
    DatePickerProps,
    'showWeekNumbers' | 'firstDayOfWeek' | 'firstWeekOfYear' | 'showMonthPickerAsOverlay' | 'allowTextInput' | 'disableAutoFocus'
> & { placeholderKey: string } = {
    ...defaultCalendarProps,
    placeholderKey: 'base.datepicker.placeholder',
    allowTextInput: true,
    disableAutoFocus: true,
}

export default function LocalDatePicker(props: LocalDatePickerProps): ReactElement {
    const { t: translate } = useTranslation()
    const strings = useCalendarStrings()
    const styles = useStyles()
    const inputRef = useRef<HTMLInputElement>()

    const [value, setValue] = useState<Date>(null)

    useEffect(() => {
        // Never undefined: the Fluent date picker turns uncontrolled on an undefined value and would keep showing the
        // date the user just cleared.
        setValue(props.isoValue ? (parseDate(props.isoValue)?.toBSON() ?? null) : null)
    }, [props.isoValue])

    const { placeholderKey, placeholderSuffix, placeholder, ...allProps } = { ...defaultDatePickerProps, ...props }

    // The date picker reads its required state from the field it sits in, so a clearable field follows the same route:
    // marking a field required is the only thing a form has to do to keep the cross away.
    const fieldContext = useFieldContext_unstable()
    const showClear = isDateFieldClearable({
        required: fieldContext?.required ?? allProps.required,
        readOnly: allProps.readOnly,
        disabled: allProps.disabled,
    })

    const clearDate = (): void => {
        props.onSelectDate?.(null)
        setValue(null)
        inputRef.current?.focus()
    }

    return (
        <DatePicker
            formatDate={formatFullNumericDate}
            parseDateFromString={(date: string) => DateTime.fromFormat(date, 'd-M-yy').toBSON()}
            {...allProps}
            ref={inputRef}
            className={mergeClasses(styles.root, !value && styles.rootEmpty, allProps.className)}
            strings={allProps.strings ?? strings}
            placeholder={[placeholder ?? translate(placeholderKey), placeholderSuffix].filter(Boolean).join(' ')}
            value={value}
            onSelectDate={(date: Date) => props.onSelectDate?.(date === null ? null : formatIsoDate(date))}
            onChange={() => {}}
            contentAfter={{
                children: (
                    <>
                        {showClear && (
                            <Tooltip content={translate('base.datepicker.clear')} relationship={'label'} withArrow>
                                <Button
                                    data-clear-date
                                    className={styles.clear}
                                    appearance={'transparent'}
                                    size={'small'}
                                    icon={<DismissIcon />}
                                    // Keeps the focus in the field instead of handing it to the button.
                                    onMouseDown={event => event.preventDefault()}
                                    // Both the icon slot and the field itself open the calendar on a click.
                                    onClick={event => {
                                        event.stopPropagation()
                                        clearDate()
                                    }}
                                />
                            </Tooltip>
                        )}
                        <CalendarMonthRegular />
                    </>
                ),
            }}
        />
    )
}
